"use client"

import { getFixedT } from "@/languages/i18n"
import { Alert, Form } from "antd"
import { useIcoData } from "../hooks/use-ico-data"
import { useRegistrationForm } from "../hooks/use-registration-form"
import { FormField } from "./FormField"
import { IcoField } from "./IcoField"
import { PolicyCheckbox } from "./PolicyCheckbox"

export const RegistrationFormContent = ({
  className = "",
  linkTermsOfService,
  emailTo,
  locale,
}: {
  className?: string
  linkTermsOfService?: string
  emailTo?: string
  locale?: string
}) => {
  const t = getFixedT(locale)
  const [form] = Form.useForm()

  const { fetchingIco, handleFetchIcoData, icoInputRef, resetIcoData, errorMessage, clearError } = useIcoData(form, locale)

  const { loading, showAlert, isMessageSent, handleSubmit, closeAlert } = useRegistrationForm({
    form,
    onResetIcoData: resetIcoData,
    emailTo,
  })

  return (
    <div className={`w-full ${className}`}>
      <Form form={form} layout="vertical" onFinish={handleSubmit} className="form-suppliers flex flex-col gap-[28px]">
        <div className="grid grid-cols-1 sm:grid-cols-2 sm:gap-[28px] gap-4">
          <FormField
            locale={locale}
            name="fullname"
            label={<span className="text-base leading-[24.8px] text-[#212529] font-medium">{t("Full Name")}</span>}
          />
          <FormField
            locale={locale}
            name="position"
            label={<span className="text-base leading-[24.8px] text-[#212529] font-medium">{t("Position")}</span>}
          />
        </div>

        <FormField
            locale={locale}
          name="companyName"
          label={
            <span className="text-base leading-[24.8px] text-[#212529] font-medium">
              {t("Company name")} <span className="text-red-500">*</span>
            </span>
          }
          required
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 sm:gap-[28px] gap-4">
          <IcoField
            locale={locale}
            icoInputRef={icoInputRef}
            fetchingIco={fetchingIco}
            onFetchIcoData={handleFetchIcoData}
            errorMessage={errorMessage}
            onClearError={clearError}
          />
          <FormField
            locale={locale}
            name="dic"
            label={<span className="text-base leading-[24.8px] text-[#212529] font-medium">{t("DIČ")}</span>}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 sm:gap-[28px] gap-4">
          <FormField
            locale={locale}
            name="phone"
            label={
              <span className="text-base leading-[24.8px] text-[#212529] font-medium">
                {t("Phone number")} <span className="text-red-500">*</span>
              </span>
            }
            required
            rules={[{ pattern: /^[a-zA-Z0-9]+$/, message: t("Số điện thoại phải là 0-9") }]}
          />
          <FormField
            locale={locale}
            name="email"
            label={
              <span className="text-base leading-[24.8px] text-[#212529] font-medium">
                {t("Email address")} <span className="text-red-500">*</span>
              </span>
            }
            type="email"
            required
            rules={[
              {
                pattern:
                  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                message: t("Please enter a valid email"),
              },
            ]}
          />
        </div>

        <FormField
            locale={locale}
          name="address"
          label={<span className="text-base leading-[24.8px] text-[#212529] font-medium">{t("Company address")}</span>}
        />

        <FormField
            locale={locale}
          name="notes"
          label={<span className="text-base leading-[24.8px] text-[#212529] font-medium">{t("Notes / Message")}</span>}
          type="textarea"
          rows={4}
        />

        <PolicyCheckbox linkTermsOfService={linkTermsOfService} locale={locale} />

        <div className="flex mt-2">
          <button
            type="submit"
            disabled={loading}
            className="max-w-[428px] mx-auto w-full px-3 h-[62px] bg-[#00A651] rounded text-white hover:bg-[#008a43] transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold"
          >
            {loading ? t("Loading...") : t("Submit form")}
          </button>
        </div>
      </Form>

      {showAlert && (
        <div className="mt-4">
          <Alert
            description={isMessageSent ? t("Form submitted successfully. Thank you!") : t("Something went wrong")}
            type={isMessageSent ? "success" : "error"}
            closable
            onClose={closeAlert}
          />
        </div>
      )}
    </div>
  )
}
